import * as R from 'remeda'
import { IconCaretDownFilled, IconPlus, IconSparkles } from '@tabler/icons-react'
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material'
import { useChat } from '../hooks/useChat'
import { useListChats } from '../hooks/useListChats'
import { wollemAgentApi } from '../api'
import { ChatInput } from './ChatInput'
import { ChatMessages } from './ChatMessages'
import { SimpleDrawer } from '~/ui/components/SimpleDrawer'
import type { ChatRecord } from '../api'
import { useUsage } from '../hooks/useUsage'

interface Props {
  open: boolean
  onClose: () => void
}
const TRUNCATE_VALUE = 30

const getChatLabel = (chat: ChatRecord): string => {
  if (chat.summary) return R.truncate(chat.summary, TRUNCATE_VALUE)

  const firstUserMsg = chat.messages.find((m) => m.role === 'user')
  if (firstUserMsg) return R.truncate(firstUserMsg.content, TRUNCATE_VALUE)

  return '-'
}

export const AgentDrawer = ({ open, onClose }: Props) => {
  const { chatId, messages, isStreaming, sendMessage, stop, clear, loadMessages } = useChat()
  const { data: chats } = useListChats(open)

  const handleSelectChat = async (selectedId: string) => {
    if (selectedId === chatId) return
    const chat = await wollemAgentApi.getChat(selectedId)
    loadMessages(chat)
  }

  const { data: usage } = useUsage()
  return (
    <SimpleDrawer
      open={open}
      onClose={onClose}
      title="Wollem Agent"
      icon={
        <SvgIcon fontSize="small" color="primary">
          <IconSparkles />
        </SvgIcon>
      }
    >
      {/* Header */}
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          px: 2,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
        spacing={1}
        useFlexGap
      >
        <Tooltip title="New conversation">
          <span>
            <IconButton
              size="small"
              onClick={clear}
              disabled={isStreaming || messages.length === 0}
            >
              <SvgIcon fontSize="small">
                <IconPlus />
              </SvgIcon>
            </IconButton>
          </span>
        </Tooltip>
        {chats && chats.length > 0 ? (
          <Select
            value={chatId ?? ''}
            onChange={(e) => handleSelectChat(e.target.value)}
            variant="standard"
            disableUnderline
            displayEmpty
            renderValue={(value) => {
              const chat = chats.find((c) => c.chat_id === value)
              return (
                <Typography variant="subtitle2" color="text.secondary">
                  {chat ? getChatLabel(chat) : 'New conversation'}
                </Typography>
              )
            }}
            IconComponent={(props) => {
              return (
                <SvgIcon fontSize="small" color="action" sx={{ width: 15, height: 15 }} {...props}>
                  <IconCaretDownFilled stroke={1.5} />
                </SvgIcon>
              )
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            {chats.map((chat) => (
              <MenuItem key={chat.chat_id} value={chat.chat_id}>
                <Box>
                  <Typography variant="body2">{getChatLabel(chat)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(chat.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            Wollem Assistant
          </Typography>
        )}
        {usage ? (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Est. cost this month <strong>${R.floor(usage.estimatedCostUsd, 2)}</strong>
          </Typography>
        ) : null}
      </Stack>

      {/* Body */}
      <ChatMessages messages={messages} />

      {/* Input */}
      <ChatInput onSend={sendMessage} onStop={stop} isStreaming={isStreaming} disabled={false} />
    </SimpleDrawer>
  )
}
